// src/components/shopper/ShopperCheckout.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import { useCart } from './ShopperCart';
import ShopperStripe from '../payments/ShopperStripe';

// ——— EU countries list (lowercase) ———
const EU_COUNTRIES = [
  'austria','belgium','bulgaria','croatia','cyprus','czech republic',
  'denmark','estonia','finland','france','germany','greece','hungary',
  'ireland','italy','latvia','lithuania','luxembourg','malta','netherlands',
  'poland','portugal','slovakia','slovenia','spain','sweden','romania',
];

// ——— Region-specific parcel rates (online prices in EUR) ———
const RATES = {
  DE: [
    { maxKg: 2,  cost:  7.69 },
    { maxKg: 5,  cost:  8.69 },
    { maxKg: 10, cost: 10.49 },
    { maxKg: 20, cost: 17.49 },
  ],
  EU: [
    { maxKg: 2,  cost: 15.49 },
    { maxKg: 5,  cost: 18.49 },
    { maxKg: 10, cost: 22.49 },
    { maxKg: 20, cost: 46.99 },
  ],
  WW: [
    { maxKg: 2,  cost: 27.49 },
    { maxKg: 5,  cost: 46.99 },
    { maxKg: 10, cost: 61.99 },
    { maxKg: 20, cost:100.99 },
  ],
};

// Stripe Processing Fees (estimator)
const STRIPE_FEE_PERCENT_EU    = 0.014;
const STRIPE_FEE_PERCENT_OTHER = 0.029;
const STRIPE_FEE_FIXED_CENTS   = 25;   // €0.25
const HIGHEST_RATE             = 0.0325; // 3.25% for non-UK international cards
const STRIPE_FIXED_CENTS       = 25;     // €0.25

// Email Validator
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());


function getRegion(country) {
  if (!country) return 'DE';
  const c = country.trim().toLowerCase();
  if (c === 'germany' || c === 'deutschland') return 'DE';
  if (EU_COUNTRIES.includes(c)) return 'EU';
  return 'WW';
}

function getStripeFeeRate(country) {
  const isEU = EU_COUNTRIES.includes(country.trim().toLowerCase());
  return isEU ? STRIPE_FEE_PERCENT_EU : STRIPE_FEE_PERCENT_OTHER;
}

/** Compute shipping cost (with 20% surcharge). */
function getShippingCost(region, weightKg) {
  const tiers = RATES[region];
  if (weightKg > 20) {
    const parcels = Math.ceil(weightKg / 20);
    const bracket20 = tiers.find(p => p.maxKg === 20);
    const base20    = bracket20 ? bracket20.cost : 0;
    return Math.round(parcels * base20 * 1.2 * 100) / 100;
  }
  const bracket = tiers.find(p => weightKg <= p.maxKg) || tiers[tiers.length - 1];
  return Math.round(bracket.cost * 1.2 * 100) / 100;
}

export default function ShopperCheckout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { items, totalPrice } = useCart();

  // --- weights & shipping
  const netWeight  = items.reduce((sum, item) => sum + (item.weight ?? 0) * item.qty, 0);
  const packWeight = 0.5;
  const shipWeight = netWeight + packWeight;

  const [country, setCountry] = useState('');
  const region       = getRegion(country);
  const shippingCost = getShippingCost(region, shipWeight);

  // --- voucher state
  const [voucher, setVoucher] = useState('');
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherError, setVoucherError] = useState('');

  // --- totals
  const subTotal = Math.round((totalPrice + shippingCost) * 100) / 100;
  const discount = voucherInfo ? Math.min(voucherInfo.amount, subTotal) : 0;
  const finalTotal = Math.round((subTotal - discount) * 100) / 100;

  const grossCents         = Math.round(finalTotal * 100);
  const estimatedFeeCents  = Math.ceil(grossCents * HIGHEST_RATE + STRIPE_FIXED_CENTS);
  const processingFee      = estimatedFeeCents / 100;            // in €
  const totalWithFee       = (grossCents + estimatedFeeCents) / 100;

  // --- form steps
  const steps = ['Account','Shipping Address','Payment Method','Review Details'];
  const [activeStep, setActiveStep] = useState(0);
  const handleNext = () => setActiveStep(s => s + 1);
  const handleBack = () => setActiveStep(s => s - 1);

  // --- Step 1: account
  const [accountOption, setAccountOption] = useState('guest');
  const [guestEmail, setGuestEmail]       = useState('');
  const [guestPhoneNumber, setGuestPhoneNumber] = useState('');
  const [signInEmail, setSignInEmail]     = useState('');
  const [signInPassword, setSignInPassword] = useState('');

  // --- Step 2: address
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [address1, setAddress1]     = useState('');
  const [city, setCity]             = useState('');
  const [postalCode, setPostalCode] = useState('');

  // --- shipping address object
  const shippingAddress = { firstName, lastName, address1, city, postalCode, country };

  // --- email for order
  const email = accountOption === 'signin' ? signInEmail : guestEmail;
  const normalizedEmail = isEmail(email) ? email.trim() : '';

  /** Returns true if the current step has all required fields filled. */
  function isStepValid() {
    if (activeStep === 0) {
      // Account step: if guest, need both email + phone; if signin, need email+password
      if (accountOption === 'guest') {
        return isEmail(guestEmail) && guestPhoneNumber.trim() !== '';
      }
      return isEmail(signInEmail) && signInPassword.trim() !== '';
    }
    if (activeStep === 1) {
      // Shipping step: require all fields
      return (
        firstName.trim()   !== '' &&
        lastName.trim()    !== '' &&
        address1.trim()    !== '' &&
        city.trim()        !== '' &&
        postalCode.trim()  !== '' &&
        country.trim()     !== ''
      );
    }
    if (activeStep === 2) {
      // Payment step is handled entirely inside ShopperStripe (its Pay button).
      // No "Next" here, and validity is always true to keep the Stepper logic happy.
      return true;
    }
    // Review step
    return true;
  }

  const handleVoucherBlur = async () => {
    const code = voucher.trim().toUpperCase().replace(/\s+/g, '');
    if (!code) return;
    setVoucherError('');
    setVoucherInfo(null);
    try {
      const res = await fetch(`/giftcards/${code}`);
      if (!res.ok) throw new Error();
      const info = await res.json();
      if (info.redeemed) {
        setVoucherError('This voucher has already been used.');
      } else {
        setVoucherInfo({ ...info, code });
      }
    } catch {
      setVoucherError('Voucher not found.');
    }
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, py:4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>Checkout</Typography>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb:4 }}>
          {steps.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Box sx={{ display:'flex', flexDirection:{xs:'column',md:'row'}, gap:2, mb:5, justifyContent:'center' }}>
          {/* LEFT PANEL */}
          <Box sx={{ width:{xs:'100%',md:'50%'} }}>
            <Paper sx={{ p:4, minHeight:'60vh', display:'flex', flexDirection:'column' }}>
              {activeStep === 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Account</Typography>
                  <Box>
                    <Box sx={{ display:'flex', gap:2, flexDirection:'column' }}>
                      {/* Simple toggle buttons could be used; keeping radios out of scope now */}
                      <Button
                        variant={accountOption === 'signin' ? 'contained' : 'outlined'}
                        onClick={() => setAccountOption('signin')}
                      >
                        Sign In
                      </Button>
                      {accountOption==='signin' && (
                        <Box sx={{ pt:1 }}>
                          <TextField
                            label="Email"
                            fullWidth
                            margin="normal"
                            value={signInEmail}
                            onChange={e => setSignInEmail(e.target.value)}
                            error={!!signInEmail && !isEmail(signInEmail)}
                            helperText={
                              signInEmail && !isEmail(signInEmail)
                                ? 'Enter a valid email like name@example.com'
                                : ' '
                            }
                          />
                          <TextField
                            label="Password"
                            type="password"
                            fullWidth
                            margin="normal"
                            value={signInPassword}
                            onChange={e => setSignInPassword(e.target.value)}
                          />
                        </Box>
                      )}

                      <Button
                        variant={accountOption === 'guest' ? 'contained' : 'outlined'}
                        onClick={() => setAccountOption('guest')}
                      >
                        Checkout as Guest
                      </Button>
                      {accountOption==='guest' && (
                        <Box sx={{ pt:1 }}>
                          <TextField
                            label="Email"
                            required
                            fullWidth
                            margin="normal"
                            value={guestEmail}
                            onChange={e => setGuestEmail(e.target.value)}
                            error={!!guestEmail && !isEmail(guestEmail)}
                            helperText={
                              guestEmail && !isEmail(guestEmail)
                                ? 'Enter a valid email like name@example.com'
                                : ' '
                            }
                          />
                          <TextField
                            label="Phone Number"
                            required
                            fullWidth
                            margin="normal"
                            value={guestPhoneNumber}
                            onChange={e => setGuestPhoneNumber(e.target.value)}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {activeStep === 1 && (
                <>
                  <Typography variant="h6" gutterBottom>Shipping Address</Typography>
                  <Box sx={{ display:'grid', gap:2 }}>
                    <TextField label="First Name" required fullWidth value={firstName} onChange={e => setFirstName(e.target.value)} />
                    <TextField label="Last Name" required fullWidth value={lastName} onChange={e => setLastName(e.target.value)} />
                    <TextField label="Address" required fullWidth value={address1} onChange={e => setAddress1(e.target.value)} />
                    <TextField label="City" required fullWidth value={city} onChange={e => setCity(e.target.value)} />
                    <TextField label="Postal Code" required fullWidth value={postalCode} onChange={e => setPostalCode(e.target.value)} />
                    <TextField label="Country" required fullWidth value={country} onChange={e => setCountry(e.target.value)} helperText="e.g. Germany, France, USA…" />
                    <TextField
                      label="Redeem voucher"
                      fullWidth
                      value={voucher}
                      onChange={e => setVoucher(e.target.value)}
                      onBlur={handleVoucherBlur}
                      helperText={voucherError || ' '}
                      error={!!voucherError}
                    />
                  </Box>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <Typography variant="h6" gutterBottom>Payment Method</Typography>

                  {/* No radio/selectors here. The options are inside ShopperStripe’s PaymentElement. */}
                  <ShopperStripe
                    amount={totalWithFee}
                    processingFee={processingFee}
                    items={items}
                    shippingCost={shippingCost}
                    email={normalizedEmail}  // guaranteed valid or empty
                    phoneNumber={guestPhoneNumber}
                    shippingAddress={shippingAddress}
                    weight={shipWeight}
                    voucherCode={voucherInfo?.code}
                  />
                  {/* No "Next" button on this step; Pay button inside Stripe handles submission */}
                </>
              )}

              {activeStep === 3 && (
                <>
                  <Typography variant="h6" gutterBottom>Review Details</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Please confirm your details. You’ll finalize payment on the previous step.
                  </Typography>
                  {/* You can add a compact summary here if you keep this step. */}
                </>
              )}

              {/* navigation buttons */}
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button disabled={activeStep === 0} onClick={handleBack}>
                  Back
                </Button>

                {/* Hide Next on the payment step (index 2) */}
                {activeStep < steps.length - 1 && activeStep !== 2 ? (
                  <Button variant="contained" onClick={handleNext} disabled={!isStepValid()}>
                    Next
                  </Button>
                ) : activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/order-confirmation')}
                    disabled={!isStepValid()}
                  >
                    Finish
                  </Button>
                ) : null}
              </Box>
            </Paper>
          </Box>

          {/* RIGHT PANEL */}
          <Box sx={{ width:{ xs:'100%', md:'50%' } }}>
            <Paper sx={{ p:4, backgroundColor:theme.palette.grey[50], minHeight:'60vh', display:'flex', flexDirection:'column' }}>
              <Typography variant="h6" gutterBottom>Order Summary</Typography>
              <List disablePadding sx={{ flexGrow:1 }}>
                {items.map(i => (
                  <ListItem key={i.id} sx={{ py:1, px:0 }}>
                    <ListItemText
                      primary={`${i.name} ×${i.qty}`}
                      secondary={`€${(parseFloat(i.price.replace(/[^\d.]/g,'')) * i.qty).toFixed(2)}`}
                    />
                  </ListItem>
                ))}
                <Divider sx={{ my:1 }} />
                <ListItem sx={{ py:1, px:0 }}>
                  <ListItemText primary={`Shipping (${shipWeight.toFixed(2)}kg)`} />
                  <Typography>€{shippingCost.toFixed(2)}</Typography>
                </ListItem>
                <Divider sx={{ my:1 }} />
                <ListItem sx={{ py:1, px:0 }}>
                  <ListItemText primary="Subtotal" />
                  <Typography>€{subTotal.toFixed(2)}</Typography>
                </ListItem>

                {discount > 0 && (
                  <>
                    <Divider sx={{ my:1 }} />
                    <ListItem sx={{ py:1, px:0 }}>
                      <ListItemText primary="Voucher" />
                      <Typography>−€{discount.toFixed(2)}</Typography>
                    </ListItem>
                  </>
                )}

                <Divider sx={{ my:1 }} />
                <ListItem sx={{ py:1, px:0 }}>
                  <ListItemText primary="Processing fee" />
                  <Typography>€{processingFee.toFixed(2)}</Typography>
                </ListItem>

                <Divider sx={{ my:1 }} />
                <ListItem sx={{ py:1, px:0 }}>
                  <ListItemText primary="Grand total" />
                  <Typography variant="subtitle1">
                    €{((grossCents + Math.round(100 * processingFee)) / 100).toFixed(2)}
                  </Typography>
                </ListItem>
              </List>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
